import { Group } from '@/components/_common';
import {
  Button,
  Descriptions,
  Divider,
  Typography,
  Upload,
  UploadFile,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useState } from 'react';
import { useWorkorderImportFromJson } from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.hooks';
import { ImportModalFormProps } from '..';
import { InsuranceClaim } from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.types';
import { cnpjMask, cpfMask } from '@/utils/mask/commonMasks';
import { applyMask } from '@/utils/mask';
import { XCircle } from 'react-feather';
import { useWindowSize } from '@/hooks';

export default function ImportJson(props: ImportModalFormProps) {
  const { isMobile } = useWindowSize();

  const [jsonData, setJsonData] = useState<InsuranceClaim>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const importFromJson = useWorkorderImportFromJson();

  const handleClose = () => {
    setJsonData(undefined);
    setFileList([]);
    props.onClose();
  };

  const handleFileRead = (file: File) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const json = JSON.parse(content);
        setJsonData(json);
      } catch {}
    };

    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!!jsonData) {
      const result = await importFromJson.mutateAsync(jsonData);

      setJsonData(undefined);
      setFileList([]);

      props.onImported({
        data: result,
        origin: props.origin,
      });
    }
  };

  const formatDocument = (documentNumber?: string) => {
    if (!documentNumber) return undefined;

    const length = documentNumber.length;
    if (length === 11) return applyMask(documentNumber, cpfMask);
    if (length === 14) return applyMask(documentNumber, cnpjMask);

    return documentNumber;
  };

  const requiredFields = [
    {
      label: 'CNPJ da empresa',
      value: jsonData?.companyCNPJ
        ? applyMask(jsonData.companyCNPJ, cnpjMask)
        : undefined,
    },
    {
      label: 'Placa do veículo',
      value: jsonData?.licensePlate?.toUpperCase(),
    },
    {
      label: 'Nome ou número do documento do cliente',
      value:
        formatDocument(jsonData?.customer?.documentNumber) ||
        jsonData?.customer?.name,
    },
    {
      label: 'CNPJ Seguradora',
      value: jsonData?.insurer?.cnpj
        ? applyMask(jsonData.insurer.cnpj, cnpjMask)
        : undefined,
    },
  ];

  const missingFields = requiredFields.filter((field) => !field.value);

  return (
    <>
      <Upload
        accept="application/json,.json"
        type="drag"
        customRequest={({ file, onError, onSuccess }) => {
          try {
            !!onSuccess && onSuccess([file]);
            handleFileRead(file as File);
          } catch (error: unknown) {
            if (error instanceof Error) {
              !!onError && onError(error);
            } else {
              !!onError && onError(new Error('Unknown error'));
            }
          }
        }}
        onRemove={() => setJsonData(undefined)}
        multiple={false}
        maxCount={1}
        fileList={fileList}
        onChange={({ fileList }) => setFileList(fileList)}
      >
        <Group gap={10} position="center">
          <UploadOutlined style={{ fontSize: 16 }} />
          Importar arquivo
        </Group>
      </Upload>

      {!!jsonData && (
        <Descriptions
          title={
            <Typography.Text strong>
              Campos obrigatórios <span style={{ color: 'red' }}>*</span>
            </Typography.Text>
          }
          bordered
          style={{ marginTop: 12 }}
          column={1}
          layout={isMobile ? 'vertical' : 'horizontal'}
          labelStyle={{ fontWeight: 'bold' }}
          size="small"
        >
          {requiredFields.map((item) => (
            <Descriptions.Item label={item.label}>
              {item.value ? item.value : <XCircle size={16} color="red" />}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      <Divider />

      <SpaceForButton justifyContent="flex-end">
        <Button block onClick={handleClose}>
          Cancelar
        </Button>

        <Button
          block
          type="primary"
          onClick={handleImport}
          loading={importFromJson.isLoading}
          disabled={!!missingFields.length}
        >
          Importar
        </Button>
      </SpaceForButton>
    </>
  );
}
